import type {FC} from 'react';
import {MdArrowForward} from 'react-icons/md';
import {useInView} from 'react-intersection-observer';
import {Carousel} from '.';
import {
  type UseInfiniteGetColumnPostList,
  useInfiniteGetColumnPostList,
} from '../../../../../core/repositories/columnRepository';
import {cn} from '../../../../../lib/classname';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {Card, CardSkeleton} from '../Card';
import {Title} from './Title';

type Props = {
  className?: string;
  title: string;
  description?: string;
  viewMoreLink?: string;
  queryParam: UseInfiniteGetColumnPostList['queryParam'];
  id?: string;
  count?: number;
};

const LIMIT = 10;

export const RegularCarousel: FC<Props> = ({
  className,
  queryParam,
  title,
  description,
  viewMoreLink: detailLink,
  id,
  count,
}) => {
  const {ref, inView} = useInView({
    triggerOnce: true,
  });
  const {data, isLoading} = useInfiniteGetColumnPostList({
    queryParam: {
      limit: LIMIT,
      ...queryParam,
    },
    config: {
      enabled: inView,
    },
  });
  const posts =
    data?.pages.filter((page) => page.ok).flatMap((page) => page.value.posts) ??
    [];
  if (data?.pages.some((page) => !page.ok)) return null;

  return (
    <div
      className={cn('w-full odd:bg-white py-16', className)}
      ref={ref}
      id={id}
    >
      <div className="flex flex-col gap-8 mx-16 pc:mx-[max(calc((100vw-1200px)/2),24px)] mb-16">
        <Title detailLink={detailLink} title={title} />
        {description && <p className="text-[14px] leading">{description}</p>}
      </div>

      <Carousel className="max-w-[1200px] ml-16 pc:mx-auto">
        <div
          className={cn('flex flex-nowrap gap-16 mr-16', 'pc:mx-0 pc:gap-24')}
        >
          <>
            {isLoading
              ? Array.from({length: 10}).map((_, index) => (
                  <CardSkeleton
                    // biome-ignore lint/suspicious/noArrayIndexKey: skelton
                    key={index}
                    className="w-[80vw] pc:w-[calc(((1200px-24px*2)/3)-30px)]"
                    imageHeight={148}
                  />
                ))
              : posts.map((post) => (
                  <Card
                    key={post.id}
                    id={post.id}
                    title={post.title}
                    clipCount={post.favoriteCount}
                    isClipped={post.favorited}
                    tagNames={post.tags}
                    thumbnail={post.cardImageURL ?? post.carouselThumbnailURL}
                    className="w-[80vw] pc:w-[calc(((1200px-24px*2)/3)-30px)]"
                    sponsoredOption={
                      post.sponsorCompanies
                        ? {
                            enabled: true,
                            companies: post.sponsorCompanies,
                          }
                        : {enabled: false}
                    }
                    updatedAt={post.updateDateTime}
                  />
                ))}

            {detailLink && posts.length === LIMIT && (
              <CommonLink
                href={detailLink}
                className={cn(
                  'flex items-center justify-center w-[200px] h-full bg-white border border-black-5 p-4 rounded-16 hover:opacity-60',
                )}
              >
                <div className="flex flex-col items-center">
                  <MdArrowForward size={32} />
                  <span className="text-[14px] text-black text-center mt-1">
                    もっと見る
                    {typeof count === 'number' && count > 0 && `（${count}件）`}
                  </span>
                </div>
              </CommonLink>
            )}
          </>
        </div>
      </Carousel>
    </div>
  );
};
